/**
 * What the world sounds like where the player is, and when: the pure rules
 * behind the beds of sound (`three/audio/world.ts`) and the odd calls in them.
 *
 * - **Wind** by its live speed, gusting between the mean and the reported
 *   peak, and louder out in the open than in a town or a wood.
 * - **Rain** by its rate: a hiss at a drizzle, a roar in a downpour, on the
 *   roof when inside a car. Thunder comes after the flash, a second for every
 *   343 m to the strike.
 * - **Town**: a hum of distant traffic and people talking in the centre, by
 *   day.
 * - **Nature**: birds by day in woods and parks, a dawn chorus at first
 *   light; crickets on a warm summer night; gulls and the wash of waves on
 *   the coast.
 * - **Bells** on the hour in town, a stroke for each hour, by day.
 */

import type { StepSurface } from './sounds.js';

/** Where the listener is and what the weather does there. */
export interface Place {
  /** How built-up, 0 country .. 1 city centre. */
  urban: number;
  /** How wooded, 0 open .. 1 forest. */
  trees: number;
  /** How much of the surroundings is sea, 0..1. */
  sea: number;
  /** Local solar hour, 0..24, and 0 day .. 1 night. */
  hour: number;
  night: number;
  /** °C. */
  temperature: number;
  /** mm/h of rain, cm/h of snow. */
  rain: number;
  snowfall: number;
  /** Wind, m/s, now (gusts included). */
  wind: number;
  /** Metres over the ground: birds and waves fade above the treetops. */
  height: number;
  /** In a tunnel: the world outside goes quiet. */
  tunnel: boolean;
}

/** How loud each bed of sound is, 0..1, and how often the calls come, a minute. */
export interface Beds {
  wind: number;
  /** The wind's colour: its band-pass, Hz, higher as it blows harder. */
  windPitch: number;
  rainLight: number;
  rainHeavy: number;
  roof: number;
  town: number;
  chatter: number;
  waves: number;
  crickets: number;
  birdsPerMinute: number;
  gullsPerMinute: number;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const smooth = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};

/** How much of the day it is light enough for birds: 0 at night, 1 by day, a peak around dawn. */
export function birdTime(hour: number): { day: number; dawn: number } {
  const day = smooth(4, 6, hour) * (1 - smooth(20.5, 22.5, hour));
  // The chorus starts with the first light, not before.
  const dawn = Math.exp(-(((hour - 5.5) / 1.2) ** 2)) * smooth(3.5, 4.5, hour);
  return { day, dawn };
}

/** The beds at `place`; `inside` a car's cabin, rain drums on the roof instead. */
export function bedsAt(place: Place, inside: boolean): Beds {
  const open = 1 - 0.5 * place.urban - 0.35 * place.trees;
  const hidden = place.tunnel ? 0.08 : 1;
  const wind = hidden * clamp01((place.wind - 1.5) / 16) * (0.4 + 0.6 * open + 0.3 * smooth(30, 200, place.height));
  const rain = place.tunnel || place.rain <= 0.05 ? 0 : 1;
  const heavy = smooth(2, 9, place.rain);
  const light = clamp01(place.rain / 1.5) * (1 - 0.6 * heavy);
  const { day, dawn } = birdTime(place.hour);
  const low = 1 - smooth(25, 120, place.height);
  const fair = 1 - smooth(0.5, 4, place.rain) - 0.5 * smooth(9, 16, place.wind);
  const busy = day * (0.4 + 0.6 * (1 - place.night));
  const town = hidden * low * place.urban * (0.35 + 0.65 * busy);
  return {
    wind,
    windPitch: 300 + 55 * place.wind,
    rainLight: (inside ? 0.3 : 1) * rain * light,
    rainHeavy: (inside ? 0.3 : 1) * rain * heavy,
    roof: inside ? rain * clamp01(0.3 + place.rain / 6) : 0,
    town,
    chatter: town * smooth(0.45, 0.85, place.urban) * busy * clamp01(fair),
    waves: hidden * low * smooth(0.05, 0.4, place.sea) * (0.6 + 0.4 * clamp01(place.wind / 12)),
    crickets: hidden * low * place.night * smooth(13, 18, place.temperature) * (1 - place.urban) * (place.rain > 0.2 ? 0 : 1),
    birdsPerMinute: hidden * low * clamp01(fair) * (day * (2 + 10 * place.trees) * (1 - 0.6 * place.urban) + dawn * 14 * (0.3 + place.trees)),
    gullsPerMinute: hidden * smooth(0.05, 0.4, place.sea) * day * 3 * clamp01(fair + 0.3),
  };
}

/**
 * The wind now: the mean, gusting toward the peak. `gust` is a slowly
 * wandering 0..1 the caller keeps (a smoothed random walk); squared, so
 * most of the time is near the mean and the gusts stand out.
 */
export function windNow(mean: number, peak: number, gust: number): number {
  return mean + (Math.max(mean, peak) - mean) * gust * gust;
}

/** Seconds from a lightning flash to its thunder, `distance` metres off. */
export function thunderDelay(distance: number): number {
  return distance / 343;
}

/** Whether a strike `distance` metres off is near enough to crack, not just roll. */
export function thunderIsNear(distance: number): boolean {
  return distance < 1500;
}

/**
 * What a footstep lands on: snow where it lies, tarmac on a built surface
 * (a puddle in the rain, now and then), else grass, or gravel round town.
 * `roll` is 0..1 for the odd puddle.
 */
export function stepSurface(
  onBuilt: boolean, snowDepth: number, rain: number, urban: number, trees: number, roll: number,
): StepSurface {
  if (snowDepth > 0.02) return 'snow';
  if (onBuilt) return rain > 0.5 && roll < 0.25 ? 'puddle' : 'asphalt';
  if (urban > 0.35 && trees < 0.3) return 'gravel';
  return 'grass';
}

/**
 * Footfalls between two gait phases: the walk cycle (`core/character/gait`)
 * runs 0..1 a stride, one foot down at 0 and the other at ½.
 */
export function footfalls(before: number, after: number): number {
  const steps = (p: number) => Math.floor(p * 2);
  let n = steps(after) - steps(before);
  if (after < before) n += 2;
  return Math.max(0, n);
}

/**
 * Strokes of the hour bell when the clock passes from `before` to `after`
 * (local hours, 0..24): one for each hour, 12 at noon, rung only from 7 in
 * the morning to 9 at night. 0 if no hour was passed.
 */
export function bellStrokes(before: number, after: number): number {
  if (Math.floor(after) === Math.floor(before) || after < before) return 0;
  const hour = Math.floor(after);
  if (hour < 7 || hour > 21) return 0;
  return hour % 12 === 0 ? 12 : hour % 12;
}
